import React, { useContext } from 'react';
import { Route, Redirect, RouteProps } from 'react-router-dom';
import CustomContext from '../../context/CustomContext';
import { AuthorityType } from '../../model/Types';
import { CustomAuth } from '../../model/Entities';
import { SubscriptionPackage } from '../../model/v2/TypesV2';

/**
 * This component represents a protected route. It redirect to the home page if the user is not authorized to view a route.
 * @param props - the route props
 */
const ProtectedRoute: React.FC<Props> = (props: Props) => {
    const context = useContext(CustomContext);
    const { authorities, subscription, subscriptionExcludedAuthorities } = props;
    const { auth } = context;

    // if user is authenticated
    if (auth && (!authorities || auth.authorities.some((a) => authorities.includes(a)))) {
        // if subscription status is not valid, redirect to subcription route
        if (
            subscription &&
            isSubscriptionRequired(auth, subscriptionExcludedAuthorities) &&
            (auth.subscriptionStatus === 'NOT_PAID' || !isSubscriptionPackageAllowed(auth, subscription))
        ) {
            return <Redirect to="/subscription" />;
        }
        // otherwise
        else {
            return <Route {...props} />;
        }
    }
    // otherwise redirect to default route
    else {
        return <Redirect to="/" />;
    }
};
export default ProtectedRoute;

interface Props extends RouteProps {
    authorities?: AuthorityType[];
    subscription?: SubscriptionPackage;
    subscriptionExcludedAuthorities?: AuthorityType[];
}

/**
 * Returns if subscription is required.
 * @param auth - the custom auth
 * @param subscriptionExcludedAuthorities - the authorities to be ignored for subscription
 * @returns if subscription is required
 */
const isSubscriptionRequired = (auth: CustomAuth, subscriptionExcludedAuthorities?: AuthorityType[]): boolean => {
    const authorities: AuthorityType[] = auth.authorities || [];
    const adminAutorities: AuthorityType[] = ['ROLE_ADMIN', 'ROLE_ORGANIZATION'];
    const excludedAutorities: AuthorityType[] = subscriptionExcludedAuthorities || [];

    return !authorities.some((a) => adminAutorities.includes(a) || excludedAutorities.includes(a));
};

/**
 * Returns if subscription package is allowed.
 * @param auth - the custom auth
 * @param subscriptionPackage - the subscription package
 * @returns if subscription package is allowed
 */
const isSubscriptionPackageAllowed = (auth: CustomAuth, subscriptionPackage: SubscriptionPackage): boolean => {
    let isSubscriptionPackageAllowed: boolean = true;
    if (auth.subscriptionPackage === 'RECRUITMENT' && !['PERFORMANCE', 'RECRUITMENT'].includes(subscriptionPackage)) {
        isSubscriptionPackageAllowed = false;
    } else if (auth.subscriptionPackage === 'PERFORMANCE' && !['PERFORMANCE'].includes(subscriptionPackage)) {
        isSubscriptionPackageAllowed = false;
    }

    return isSubscriptionPackageAllowed;
};
